import {
  CachedCaseListElement,
  Case,
  CaseListService,
  CaseReevaluationService,
  CaseServiceInterface,
  ContainerTypes,
  CurrentCaseServiceInterface,
  CurrentContextService,
  Helpers,
  NamedBindings,
  Stringifier,
  getContainer,
} from "@dandelion/core";
import axios from "axios";
import { inject, injectable } from "inversify";
import { UserService } from "./UserService";
import { ImportedData } from "../entities/ImportedData";
import { Users } from "../enum/Users";
import { ref } from "vue";
import { AdmissionIdAndHash, ImportHashCacheStorage } from "../repository/ReadmissionChainForPatientKeyStorage";
import { ImportHashRestStorage } from "../repository/ReadmissionChainForPatientKeyRestStorage";
import { YearService } from "./YearService";
import { NamedServices } from "../enum/NamedServices";
import _, { cloneDeep } from "lodash";

@injectable()
export class ImporterService {
  private IMPORT_BASE_URL = import.meta.env.VITE_APP_API_IMPORT_BASE_URL;
  public showUnchangeds = ref(false);
  public isImporting = ref(false);
  public clonedModel;
  public static key: symbol = Symbol.for('ImportChoice');
  private useLocalStorage = import.meta.env.VITE_APP_LOCAL_STORAGE && import.meta.env.VITE_APP_LOCAL_STORAGE == 'true';
  private importerHashStorage: ImportHashCacheStorage | ImportHashRestStorage = this.useLocalStorage ? new ImportHashCacheStorage() : new ImportHashRestStorage();
  private caseReevaluationService: CaseReevaluationService;
  constructor(
    @inject(NamedBindings.UserService) private userService: UserService,
    @inject(NamedBindings.CurrentContextService) private currentContextService: CurrentContextService,
    @inject(NamedBindings.CurrentCaseService) private currentCaseService: CurrentCaseServiceInterface,
    @inject(NamedBindings.CaseListService) private caseListService: CaseListService,
    @inject(NamedBindings.CaseService) private caseService: CaseServiceInterface,
  ) {
    this.caseReevaluationService = getContainer(
      this.currentContextService.context,
      ContainerTypes.REEVALUATION
    ).get(NamedBindings.CaseReevaluationService);
    axios.interceptors.request.use((config) => {
      config.headers = this.userService.getHeaders();
      return config;
    });

    this.clonedModel = currentCaseService.getModel();
    this.clonedModel[ImporterService.key] = {};
  }

  async run() {
    const list = await this.getList();
    if (!list) {
      return;
    }
    
    await this.caseListService.updateCaseList();
    const promises = list.map(async (importedData) => {
      const isAlreadyImported = await this.checkIfAlreadyImported(importedData);
      if (isAlreadyImported) {
        return this.importUpdatedData(importedData);
      } else {
        return this.importNewData(importedData);
      }
    });
    await Promise.all(promises);
  }

  toggleUnchanged() {
    this.showUnchangeds.value = !this.showUnchangeds.value
  }

  // TODO: localstorage
  async getList(): Promise<ImportedData[]> {
    const centreCode = this.userService.getCurrentUser()?.value?.centre || '*';
    const caseType = this.currentContextService.getCaseConfiguration().caseName;
    let url = `importer/list/${centreCode}/${caseType}/`;

    let data = [];
    data = (await axios.get(this.IMPORT_BASE_URL + url)).data;
    if (!Array.isArray(data)) {
      return [];
    }
    const importedData: Array<ImportedData> = data.map((d: any) => {
      return {
        caseID: d.metaData.caseID,
        data: new Stringifier<Record<string, any>>().deserialize(JSON.stringify(d.data)),
        metaData: new Stringifier<Record<string, any>>().deserialize(JSON.stringify(d.metaData)),
      };
    });
    return importedData;
  }

  async getSingleData(caseID: string): Promise<ImportedData> {
    const importedDataList = await this.getList();
    let singleData = new ImportedData({}, '', {});
    if (importedDataList) {
      singleData = importedDataList.find(data => data.caseID === caseID) ?? singleData;
    }
    return singleData;
  }

  async setAlreadyImported(caseID: string): Promise<void> {
    // TODO: This works with caseID only because both frontend and backend picks the first in the same order
    // should we consider using the row id of import row, not of case?
    const centreCode = this.userService.getCurrentUser()?.value?.centre || '*';
    const caseType = this.currentContextService.getCaseConfiguration().caseName;

    let url = `setAlreadyImported/${centreCode}/${caseID}/${caseType}/`;
    await axios.post(this.IMPORT_BASE_URL + url);
  }

  async checkIfAlreadyImported(importedDatas: ImportedData) {
    const importedDataCaseID = importedDatas.caseID;
    const list = await this.caseListService.allCases;
    const deletedList = await this.caseListService.allDeletedCases;
    return list.concat(deletedList).some((caseListElement: CachedCaseListElement) => caseListElement.caseID === importedDataCaseID)
  }

  async importNewData(importedData: ImportedData, patientKey: string = "", admissionIdAndHash?: AdmissionIdAndHash) {
    const yearService = getContainer().get(NamedServices.YearService) as YearService;
    const currentUserRole = this.userService.getCurrentUser().value?.role;
    if ((currentUserRole === Users.ADMIN || currentUserRole === Users.WRITER)) {
      const model = importedData.data;
      if (!model.consent) {
        model.consent = 'exempt';
      }
      const admissionDate = new Date(model.core.administrativeInfo.icuAdmDate).getFullYear();
      const newCurrentCaseService = getContainer(this.currentContextService.context, ContainerTypes.NEW_CASE)
        .get(NamedBindings.CurrentCaseServiceForNewCase) as CurrentCaseServiceInterface;
      yearService.setAdmissionYear(admissionDate);
      model.admissionKey = `A-${this.userService.getCurrentUser().value?.centre}-${importedData.caseID.substring(0, 15)}`;
      await newCurrentCaseService.open(importedData.caseID, model);
      await newCurrentCaseService.save();

      if (patientKey && admissionIdAndHash) {
        await this.importerHashStorage.save(patientKey, admissionIdAndHash);
      }
      await this.setAlreadyImported(importedData.caseID);
    }
  }

  async confirmUpdatedDataAfterDiff() {
    const caseMetadata = await this.caseService.getCaseMetaDataFor(this.currentCaseService.getCurrentCase().value);
    const reevaluatedCase = await this.caseReevaluationService.reevaluateCase(
      new Case(this.currentCaseService.getCurrentCase().value, this.currentCaseService.getModel(), caseMetadata)
    );
    await this.caseService.update(reevaluatedCase);
    await this.setAlreadyImported(this.currentCaseService.getCurrentCase().value);
  }

  async importUpdatedData(importedData: ImportedData) {
    if (this.currentCaseService.getCurrentCase().value == importedData.caseID) {
      return;
    }

    const yearService = getContainer().get(NamedServices.YearService) as YearService;

    const currentUserRole = this.userService.getCurrentUser().value?.role;
    if ((currentUserRole === Users.ADMIN || currentUserRole === Users.WRITER)) {
      const model = importedData.data;
      const admissionDate = new Date(model.core.administrativeInfo.icuAdmDate).getFullYear();
      yearService.setAdmissionYear(admissionDate);
      model.admissionKey = `A-${this.userService.getCurrentUser().value?.centre}-${importedData.caseID.substring(0, 15)}`;
      const defaultCase = await this.caseService.getDataFor(importedData.caseID);
      const mergedModel = cloneDeep(defaultCase.data);
      const filteredModel = filterModelUpdates(model, mergedModel);
      _.merge(mergedModel, filteredModel);
      console.log(`############### REEVALUATING ${importedData.caseID} #################`);
      const reevaluatedCase = await this.caseReevaluationService.reevaluateCase(
        new Case(defaultCase.caseID, mergedModel, defaultCase.caseMetaData)
      );

      await this.caseService.update(reevaluatedCase);
      await this.setAlreadyImported(importedData.caseID);
    }
  }

  async deleteData(caseID: string) {
    await this.setAlreadyImported(caseID);
  }
}

function filterModelUpdates(model: any, mergedModel: any, parentPath = '') {
  let filteredModel: any = {};

  for (const key in model) {

    const rawValue = model[key];
    const value = normalize(rawValue);
    const fullPath = parentPath ? `${parentPath}.${key}` : key;
    const savedRecords = Helpers.getObjByPath(fullPath, mergedModel[Symbol.for('LastSavingUser')]);


     const shouldRecurse =
      _.isPlainObject(value) && !Array.isArray(value);
    if (shouldRecurse) {
            const nested = filterModelUpdates(value, mergedModel, fullPath);

      if (Object.keys(nested).length > 0) {
        filteredModel[key] = nested;
      }
      
    } else {
      // Se esiste un record utente salvato e non è vuoto, ignoriamo l'aggiornamento
      if (!savedRecords || savedRecords.user === '') {
        filteredModel[key] = value;
      }
    }
  }

  return filteredModel;
}
function normalize(value: any) {
  if (value?.__dandelionType__ === 'ValuesSetValue') {
    return value.getValue();
  }
  return value;
}